
/* eslint-disable  @typescript-eslint/naming-convention */
import Vue from 'vue';

import RuleDetail from '@/components/dashboard/zone/RuleDetail.vue';
import LuminaireList from '@/components/dashboard/zone/LuminaireList.vue';
import SensorList from '@/components/dashboard/zone/SensorList.vue';

import {
	Device,
	Floor,
	OverrideInfoDevice,
	OverrideInfoLuminaire,
	RoomZoneDevices,
	RoomZoneDeviceTypes,
} from '@/interfaces/locations';
import { Room, Zone } from '@/interfaces/lighting';
import { DeviceType, Role } from '@/interfaces/types';
import {
	AppliedRule,
	AssignRuleForm,
	ExternalRule,
	Rule,
	RulespecMovementSettings,
	RuleTemplate,
} from '@/interfaces/rules';
import { Alert } from '@/interfaces/alerts';
import { Controller } from '@/interfaces/controllers';
import permissions from '@/lib/permissions';
import { AxiosError } from 'axios';

export default Vue.extend({
	components: {
		RuleDetail,
		LuminaireList,
		SensorList,
	},

	data(): {
		poller: number | null;
		selectedDevice: Device | undefined;
		selectedZone: Zone | null;
	} {
		return {
			poller: null,
			selectedDevice: undefined,
			selectedZone: null,
		};
	},

	computed: {
		zone(): Zone | null {
			this.$store.dispatch('locations/zones/selectZone', {
				zone: this.$store.getters['locations/zones/getZone'](this.$route.params.zoneId),
			});
			return this.$store.getters['locations/zones/getSelectedZone'];
		},

		userCanReadRules(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rules',
				'read',
			]);
		},

		userCanReadLuminaires(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'luminaires',
				'read',
			]);
		},

		userCanWriteRules(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'rules',
				'write',
			]);
		},

		userCanWriteLuminaires(): boolean {
			const role: Role = this.$store.getters['usermanagement/user/getRole'];

			return (
				permissions.hasPermission(role, ['luminaires', 'write']) &&
				permissions.hasPermission(role, ['rooms', 'write'])
			);
		},

		userCanWriteSwitches(): boolean {
			const role: Role = this.$store.getters['usermanagement/user/getRole'];

			return (
				permissions.hasPermission(role, ['switches', 'write']) &&
				permissions.hasPermission(role, ['rooms', 'write'])
			);
		},

		zones(): Zone[] | [] {
			return this.$store.getters['locations/zones/getZones'];
		},

		zonesDevices(): number[] {
			const devicesId = this.$store.getters['locations/zones/getZones'].map(
				(zone: Zone) => zone.devices
			);
			return [].concat.apply([], devicesId);
		},

		room(): Room | undefined {
			if (this.zone) {
				return this.$store.getters['locations/rooms/getRoom'](this.zone.fk_room);
			}
			return undefined;
		},

		rules(): RuleTemplate[] {
			return this.$store.getters['activities/rules/getRuleTemplates'].filter(
				(rt: RuleTemplate) => rt.fk_rule_type === 'lighting'
			);
		},

		appliedRule(): Rule | undefined {
			return this.$store.getters['activities/rules/getRules'].find(
				(r: Rule) => r.fk_group === this.zone!.id && r.fk_rule_type !== 'calibration_rule'
			);
		},

		roomRules(): Rule[] | undefined {
			return this.$store.getters['activities/rules/getRules'].filter(
				(r: Rule) => r.fk_group !== null
			);
		},

		appliedRuleAncestors(): number[] | undefined {
			return this.$store.getters['activities/rules/getRules']
				.filter((r: ExternalRule) => r.fk_parent === this.appliedRule?.id)
				.map((r: Rule) => r?.id);
		},

		ruleApplications(): AppliedRule[] {
			return this.$store.getters['activities/appliedRules/getAppliedRules'].filter(
				(r: AppliedRule) =>
					r.fk_rule === this.appliedRule?.id || this.appliedRuleAncestors?.includes(r.fk_rule)
			);
		},

		floor(): Floor | undefined {
			if (this.room) {
				return this.$store.getters['locations/floors/getFloor'](this.room.fk_area);
			}
			return undefined;
		},

		roomDevicesWithMovement(): RoomZoneDevices {
			const roomZones: Zone[] = this.zones.filter((zone) => this.room!.id === Number(zone.fk_room));
			return Object.assign(
				{},
				...roomZones.map((zone) => ({
					[zone.name]: this.$store.getters['locations/zones/getZoneLuminaires'](zone.id).concat(
						this.$store.getters['locations/zones/getZoneSwitches'](zone.id)
					),
				}))
			);
		},

		luminaires(): OverrideInfoLuminaire[] {
			if (this.zone) {
				const luminaires: Device[] = this.$store.getters['locations/zones/getZoneLuminaires'](
					this.zone.id
				);
				return luminaires.map((luminaire: Device) => {
					const activeIdentifyRule = this.$store.getters[
						'activities/appliedRules/getDeviceAppliedRule'
					](luminaire.id, this.identifyRule?.id);
					const activeOffRule = this.$store.getters['activities/appliedRules/getDeviceAppliedRule'](
						luminaire.id,
						this.offRule?.id
					);
					return {
						...luminaire,
						identifyActive: !!activeIdentifyRule,
						offActive: !!activeOffRule,
						unassign: 'on',
					};
				});
			} else {
				return [];
			}
		},

		luminairesWithOffset(): OverrideInfoLuminaire[] {
			return this.luminaires.filter(
				(luminaire) => luminaire.light_level_offset && luminaire.light_level_offset !== 0
			);
		},

		unassignedFloorLuminaires(): Device[] {
			if (this.floor) {
				return this.$store.getters['devices/luminaires/getFloorLuminaires'](this.floor.id)
					.filter(
						(luminaire: Device) =>
							!this.zonesDevices.includes(luminaire.id) && luminaire.is_luminaire
					)
					.map((luminaire: Device) => {
						const guid = this.controllers
							.find((controller) => controller.id === luminaire.fk_controller)
							?.guid.slice(-4);
						return guid ? { ...luminaire, name: `${luminaire.name} - ${guid}` } : luminaire;
					});
			}
			return [];
		},

		unassignedFloorLuminairesAndSensors(): Device[] {
			if (this.floor) {
				return this.$store.getters['devices/luminaires/getFloorLuminaires'](this.floor.id).filter(
					(l: Device) => !this.zonesDevices.includes(l.id)
				);
			}
			return [];
		},

		unassignedFloorSwitches(): Device[] {
			if (this.floor) {
				return this.$store.getters['devices/switches/getFloorSwitches'](this.floor.id).filter(
					(l: Device) => !this.zonesDevices.includes(l.id)
				);
			}
			return [];
		},

		switches(): Device[] {
			return this.$store.getters['devices/switches/getSwitches'];
		},

		emergencySwitches(): number[] {
			let correctFeatureIds: number[] = [];
			for (const deviceType of this.deviceTypes) {
				if (deviceType.features.includes('user_button_resource')) {
					correctFeatureIds.push(deviceType.id);
				}
			}
			return correctFeatureIds;
		},

		assignableEmergencySwitches(): Device[] {
			let emergencySwitch: Device[] = [];
			let devices: Device[] = [];
			for (const device of this.switches) {
				if (device.fk_device_type && this.emergencySwitches.includes(device.fk_device_type)) {
					devices.push(device);
					emergencySwitch = devices.map((p) => ({ ...p, type: 'Emergency switch' }));
				}
			}
			return emergencySwitch;
		},

		roomMovementDevices(): RoomZoneDeviceTypes {
			const movementTypes: DeviceType[] = this.deviceTypes;
			const movementTypeIds: number[] = movementTypes.map((t) => t.id);
			const roomMovementDevices: RoomZoneDevices = this.roomDevicesWithMovement;

			for (const roomDeviceKey of Object.keys(this.roomDevicesWithMovement)) {
				roomMovementDevices[roomDeviceKey] = roomMovementDevices[roomDeviceKey]
					.filter((l) => Boolean(l.fk_device_type))
					.filter((l) => movementTypeIds.includes(l.fk_device_type!));
			}

			return {
				devices: roomMovementDevices,
				types: movementTypes,
			};
		},

		zoneSwitches(): Device[] {
			if (this.zone) {
				return this.$store.getters['locations/zones/getZoneSwitches'](this.zone.id);
			} else {
				return [];
			}
		},

		roomSensors(): string[] | undefined {
			let names: string[] = [];
			let rules: Rule[] | undefined = this.roomRules;

			if (rules) {
				let ruleBody = rules.map((r) => r.rulespec[0].rules);
				for (let rule of ruleBody) {
					for (let rulePart of rule) {
						if (['movement_settings', 'relative_light_settings'].includes(rulePart['type'])) {
							for (let sensor of (rulePart as RulespecMovementSettings)['sensors']) {
								names.push(sensor);
							}
						}
					}
				}
			}
			return names;
		},

		deviceTypes(): DeviceType[] {
			return this.$store.getters['types/deviceTypes/getDeviceTypes'];
		},

		luminaireTypes(): DeviceType[] {
			return this.deviceTypes.filter((dt) => dt.category === 'luminaires');
		},

		activeAlerts(): Alert[] {
			return this.$store.getters['activities/alerts/getActiveAlerts'];
		},

		controllers(): Controller[] {
			return this.$store.getters['controllers/controllers/getControllers'];
		},

		identifyRule(): Rule | undefined {
			return (
				this.$store.getters['activities/rules/getRulesCertainType']('lighting_override') as Rule[]
			).find((rule) => rule.name === 'Lighting identify override rule');
		},

		offRule(): Rule | undefined {
			return (
				this.$store.getters['activities/rules/getRulesCertainType']('lighting_override') as Rule[]
			).find((rule) => rule.name === 'Lighting off override rule');
		},
	},

	methods: {
		back(): void {
			this.$router.back();
		},

		backToDashboard(): void {
			this.$router.push('/dashboard');
		},

		async assignRule(contents: AssignRuleForm): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				let selectedSensors: Device[] = [];
				let sensors: string[] = [];
				let movementSensors: string[] = [];
				let selectedSensor: Device | undefined = undefined;
				let lightingSensor: string | null = null;
				if (contents.movement_devices && contents.movement_devices.length) {
					for (const roomDeviceWithMovementKey of Object.keys(this.roomMovementDevices.devices)) {
						selectedSensors = [
							...selectedSensors,
							...this.roomMovementDevices.devices[roomDeviceWithMovementKey]
								.filter((l) => contents.movement_devices!.includes(l.id))
								.filter((l) => l.fk_controller !== null),
						];
					}
					movementSensors = selectedSensors.map(
						(l) =>
							this.$store.getters['controllers/controllers/getController'](l.fk_controller)?.guid
					);
					sensors = sensors.concat(movementSensors);
				}
				if (contents.lighting_device) {
					for (const roomDeviceWithMovementKey of Object.keys(this.roomMovementDevices.devices)) {
						selectedSensor = this.roomMovementDevices.devices[roomDeviceWithMovementKey].find(
							(l) => contents.lighting_device === l.id && l.fk_controller !== null
						);
						if (selectedSensor) {
							break;
						}
					}
					lightingSensor = this.$store.getters['controllers/controllers/getController'](
						selectedSensor!.fk_controller
					)?.guid;

					if (lightingSensor) {
						sensors.push(lightingSensor);
					}
				}

				await this.$store.dispatch('activities/rules/applyFromTemplate', {
					templateId: contents.rule!,
					sensors,
					movementSensors,
					lightingSensor,
					zoneId: this.zone!.id,
				});
				this.$store.dispatch('activities/rules/fetchRules');
				this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
				this.$bvModal.hide('modal-assign-rule');
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-assign-rule',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async onRuleRemove(): Promise<void> {
			if (typeof this.appliedRule !== 'undefined') {
				this.$store.dispatch('tools/loading/changeLoadingStatus');
				try {
					await this.$store.dispatch('activities/appliedRules/removeAppliedRuleGroup', {
						zoneId: this.zone!.id,
					});
				} catch (error) {
					this.$store.dispatch('tools/messageHandling/addErrorMessage', {
						id: 'error-remove-applied-rule',
						message: (error as AxiosError<{ message: string }>).response!.data.message,
					});
				}
				this.$store.dispatch('tools/loading/changeLoadingStatus');
			}
		},

		async assignDevice(deviceId: number): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			const promises = [];

			const luminaireOrSensor = this.unassignedFloorLuminairesAndSensors.find(
				(l) => l.id === deviceId
			);
			const _switch = this.unassignedFloorSwitches.find((l) => l.id === deviceId);
			const emergencySwitch = this.assignableEmergencySwitches.filter(
				(l: Device) => l.id === deviceId
			);
			const devices = [...this.zone!.devices, deviceId];

			promises.push(
				this.$store.dispatch('locations/zones/modifyZone', {
					id: this.zone!.id,
					payload: { devices },
				})
			);
			if (luminaireOrSensor && luminaireOrSensor.fk_area !== this.floor!.id) {
				promises.push(
					this.$store.dispatch('devices/luminaires/modifyLuminaire', {
						id: deviceId,
						payload: { fk_area: this.floor!.id },
					})
				);
			}

			if (_switch && _switch.fk_area !== this.floor!.id) {
				promises.push(
					this.$store.dispatch('devices/switches/modifySwitch', {
						id: deviceId,
						payload: { fk_area: this.floor!.id },
					})
				);
			}

			if (emergencySwitch.length === 1) {
				promises.push(
					this.$store.dispatch('devices/switches/modifySwitch', {
						id: deviceId,
						payload: { fk_area: this.floor!.id },
					})
				);
			}

			try {
				await Promise.all(promises);
				this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
				this.$store.dispatch('devices/luminaires/fetchLuminaires');
				this.$store.dispatch('devices/switches/fetchSwitches');
				this.$bvModal.hide('modal-assign-device');
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-assign-device',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async unassignDevice(deviceId: number): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				let allDevices: OverrideInfoDevice[] = [...this.luminaires, ...this.zoneSwitches];
				const devices = this.zone!.devices.filter((d) =>
					allDevices.map((l) => l.id).includes(d)
				).filter((d) => d !== deviceId);
				if (devices !== this.zone!.devices) {
					await this.$store.dispatch('locations/zones/modifyZone', {
						id: this.zone!.id,
						payload: { devices },
					});
				}
				this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
				this.$store.dispatch('devices/luminaires/fetchLuminaires');
				this.$store.dispatch('devices/switches/fetchSwitches');
				this.$bvModal.hide('modal-unassign-device');
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-unassign-device',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async onDeviceModify(
			device: Partial<Device> & { id: number },
			type: Partial<Device> & { id: number }
		): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				switch (type.category) {
					case 'switches':
						await this.$store.dispatch('devices/switches/modifySwitch', {
							id: device.id,
							payload: device,
						});
						break;
					case 'luminaires':
						await this.$store.dispatch('devices/luminaires/modifyLuminaire', {
							id: device.id,
							payload: device,
						});
						break;
				}
				this.$store.dispatch('activities/rules/fetchRules');
				this.$store.dispatch('activities/appliedRules/fetchAppliedRules');
				this.$bvModal.hide('modal-device-edit');
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-modify-luminaire',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},
	},

	mounted() {
		this.poller = window.setInterval(
			() => this.$store.dispatch('activities/appliedRules/fetchAppliedRules'),
			5000
		);
	},

	destroyed() {
		if (this.poller) {
			window.clearInterval(this.poller);
			this.poller = null;
		}
		this.$store.dispatch('locations/zones/cleanSelectedZone');
	},
});
